import { useState, useEffect, useCallback, useRef } from 'react'

interface VADOptions {
  onSpeechStart?: () => void
  onSpeechEnd?: () => void
  positiveSpeechThreshold?: number
  negativeSpeechThreshold?: number
  minSpeechFrames?: number
  preSpeechPadFrames?: number
  redemptionFrames?: number
  frameSamples?: number
  modelURL?: string
  modelFetcher?: (path: string) => Promise<ArrayBuffer>
  ortConfig?: any
  // Speech recording integration
  enableSpeechRecording?: boolean
  onStartRecording?: () => void
  onStopRecording?: () => void
}

interface UseProperVADReturn {
  isVADSupported: boolean
  isVADInitialized: boolean
  isListening: boolean
  isSpeaking: boolean
  isInitializing: boolean
  error: string | null
  startListening: () => Promise<void>
  stopListening: () => void
  testEnvironment: () => Promise<{ isNoisy: boolean; recommendation: string }>
}

export const useProperVAD = (options: VADOptions = {}): UseProperVADReturn => {
  const [isVADSupported] = useState(() => {
    return !!(window.AudioContext || (window as any).webkitAudioContext)
  })
  
  const [isVADInitialized, setIsVADInitialized] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const vadInstanceRef = useRef<any>(null)
  const speechDetectionRef = useRef<boolean[]>([])
  const testTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const cleanup = useCallback(() => {
    if (vadInstanceRef.current) {
      try {
        vadInstanceRef.current.destroy()
      } catch (e) {
        console.warn('Error destroying VAD instance:', e)
      }
      vadInstanceRef.current = null
    }
    
    if (testTimeoutRef.current) {
      clearTimeout(testTimeoutRef.current)
      testTimeoutRef.current = null
    }
    
    setIsVADInitialized(false)
    setIsListening(false)
    setIsSpeaking(false)
    setIsInitializing(false)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])

  const startListening = useCallback(async () => {
    if (!isVADSupported) {
      setError('VAD is not supported in this browser')
      return
    }

    if (isListening || vadInstanceRef.current) {
      return // Already listening
    }

    try {
      setIsInitializing(true)
      setError(null)

      try {
        // Dynamic import of VAD library with proper error handling
        const vadModule = await import('@ricky0123/vad-web')
        const MicVAD = vadModule.MicVAD || vadModule.default?.MicVAD
        
        // Initialize VAD with settings similar to the original implementation
        vadInstanceRef.current = await MicVAD.new({
          onSpeechStart: () => {
            setIsSpeaking(true)
            options.onSpeechStart?.()
            if (options.enableSpeechRecording) {
              options.onStartRecording?.()
            }
          },
          onSpeechEnd: () => {
            setIsSpeaking(false)
            options.onSpeechEnd?.()
            if (options.enableSpeechRecording) {
              // Add small delay to capture end of speech
              setTimeout(() => {
                options.onStopRecording?.()
              }, 500)
            }
          },
          // Settings from the original implementation
          positiveSpeechThreshold: options.positiveSpeechThreshold ?? 0.8,
          negativeSpeechThreshold: options.negativeSpeechThreshold ?? 0.65,
          minSpeechMs: 250,
          preSpeechPadMs: 30,
          redemptionMs: 200,
          // Additional settings for better performance
          ...options
        })

        await vadInstanceRef.current.start()
        
        setIsVADInitialized(true)
        setIsListening(true)
        setIsInitializing(false)
        
        console.log('VAD initialized and started successfully')
      } catch (vadError) {
        console.warn('VAD initialization failed, VAD features will be limited:', vadError)
        setError('VAD initialization failed - speech detection unavailable')
        setIsVADInitialized(false)
        setIsListening(false)
        setIsInitializing(false)
        // Don't throw, just continue without VAD
      }
    } catch (err) {
      console.error('Failed to initialize VAD:', err)
      setError(err instanceof Error ? err.message : 'Failed to initialize VAD')
      setIsInitializing(false)
      cleanup()
    }
  }, [isVADSupported, isListening, options, cleanup])

  const stopListening = useCallback(() => {
    cleanup()
  }, [cleanup])

  const testEnvironment = useCallback(async (): Promise<{ isNoisy: boolean; recommendation: string }> => {
    return new Promise(async (resolve) => {
      if (!isVADSupported) {
        resolve({
          isNoisy: false,
          recommendation: 'VAD not supported - environment test unavailable'
        })
        return
      }

      try {
        // Try VAD first, fall back to simple volume detection if it fails
        try {
          // Reset detection array
          speechDetectionRef.current = []
          
          // Dynamic import of VAD library with proper error handling
          const vadModule = await import('@ricky0123/vad-web')
          const MicVAD = vadModule.MicVAD || vadModule.default?.MicVAD
          
          // Create temporary VAD instance for testing
          const testVAD = await MicVAD.new({
            onSpeechStart: () => {
              speechDetectionRef.current.push(true)
            },
            onSpeechEnd: () => {
              speechDetectionRef.current.push(false)
            },
            // More sensitive settings for noise detection
            positiveSpeechThreshold: 0.5,
            negativeSpeechThreshold: 0.3,
            minSpeechMs: 100,
            preSpeechPadMs: 30,
            redemptionMs: 100
          })

          await testVAD.start()

          // Test for 5 seconds
          testTimeoutRef.current = setTimeout(async () => {
            try {
              testVAD.destroy()
            } catch (e) {
              console.warn('Error destroying test VAD:', e)
            }

            // Analyze results
            const speechStartCount = speechDetectionRef.current.filter(Boolean).length
            
            // If we detected multiple speech start events without actual speech, it's likely noise
            const isNoisy = speechStartCount > 3 // More than 3 speech starts in 5 seconds indicates noise
            
            let recommendation = ''
            if (speechStartCount > 6) {
              recommendation = 'Environment is very noisy. Please find a quieter location or use headphones.'
            } else if (isNoisy) {
              recommendation = 'Some background noise detected. Consider moving to a quieter area for better accuracy.'
            } else {
              recommendation = 'Great! Your environment is suitable for the speech interview.'
            }

            resolve({ isNoisy, recommendation })
          }, 5000)
        } catch (vadError) {
          console.warn('VAD failed, falling back to simple volume detection:', vadError)
          
          // Fallback to simple volume detection
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
          const analyser = audioContext.createAnalyser()
          const microphone = audioContext.createMediaStreamSource(stream)
          
          analyser.smoothingTimeConstant = 0.8
          analyser.fftSize = 1024
          microphone.connect(analyser)
          
          const dataArray = new Uint8Array(analyser.frequencyBinCount)
          const volumeLevels: number[] = []
          
          const checkVolume = () => {
            analyser.getByteFrequencyData(dataArray)
            const volume = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length
            volumeLevels.push(volume)
          }
          
          const intervalId = setInterval(checkVolume, 100)
          
          testTimeoutRef.current = setTimeout(() => {
            clearInterval(intervalId)
            stream.getTracks().forEach(track => track.stop())
            audioContext.close()
            
            const avgVolume = volumeLevels.reduce((sum, vol) => sum + vol, 0) / volumeLevels.length
            const volumeSpikes = volumeLevels.filter(vol => vol > avgVolume + 20).length
            
            const isNoisy = avgVolume > 30 || volumeSpikes > 5
            
            let recommendation = ''
            if (avgVolume > 50) {
              recommendation = 'Environment is very noisy. Please find a quieter location or use headphones.'
            } else if (isNoisy) {
              recommendation = 'Some background noise detected. Consider moving to a quieter area for better accuracy.'
            } else {
              recommendation = 'Great! Your environment is suitable for the speech interview.'
            }
            
            resolve({ isNoisy, recommendation })
          }, 5000)
        }

      } catch (error) {
        console.error('Environment test failed:', error)
        resolve({
          isNoisy: false,
          recommendation: 'Environment test failed - please ensure microphone access is granted.'
        })
      }
    })
  }, [isVADSupported])

  return {
    isVADSupported,
    isVADInitialized,
    isListening,
    isSpeaking,
    isInitializing,
    error,
    startListening,
    stopListening,
    testEnvironment
  }
}